import * as Sentry from "@sentry/react";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle, ExternalLink } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { useOrganization } from "@/contexts/OrganizationContext";
import { quickbooksAuthErrorMessage, withoutQuickbooksAuthParams } from "@/lib/quickbooksAuthError";

interface QuickBooksAuthProps {
  onAuthChange?: (isAuthenticated: boolean) => void;
}

interface CompanyInfo {
  Name?: string;
  CompanyType?: string;
  LegalName?: string;
  Country?: string;
}

export function QuickBooksAuth({ onAuthChange }: QuickBooksAuthProps) {
  const { apiBasePath } = useOrganization();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The org's stored setting; null until it loads, and while it cannot be read —
  // the switch never shows a value the server does not hold.
  const [useHistoricalPricing, setUseHistoricalPricing] = useState<boolean | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // Set when the OAuth callback brought the admin here: the card is the last thing on
  // the Admin tab, so its result would otherwise sit below the fold.
  const showCardOnReturn = useRef(false);

  // Check authentication status and load settings on component mount
  useEffect(() => {
    if (apiBasePath) {
      checkAuthStatus();
      loadHistoricalPricingSetting();
    }
  }, [apiBasePath]);

  // Check URL params for auth callback results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const qbAuth = urlParams.get('qb_auth');
    const reason = urlParams.get('reason');
    const description = urlParams.get('description');
    
    // Clean up URL — the callback's params only. The rest (`tab=admin`) keeps this card mounted.
    const cleanedUrl = window.location.pathname + withoutQuickbooksAuthParams(window.location.search);

    if (qbAuth === 'success') {
      setError(null);
      checkAuthStatus();
      showCardOnReturn.current = true;
      window.history.replaceState({}, '', cleanedUrl);
    } else if (qbAuth === 'error') {
      console.error('QuickBooks OAuth Error:', { reason, description });
      setError(quickbooksAuthErrorMessage(reason, description));
      showCardOnReturn.current = true;
      window.history.replaceState({}, '', cleanedUrl);
    }
  }, []);

  // Back from QuickBooks: bring the card into view once it has its result to show.
  useEffect(() => {
    if (isLoading || !showCardOnReturn.current) return;
    showCardOnReturn.current = false;
    cardRef.current?.scrollIntoView({ block: 'center' });
  }, [isLoading]);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use tenant-scoped API path
      const authStatusUrl = apiBasePath ? `${apiBasePath}/quickbooks/auth-status` : getApiPath("/api/quickbooks/auth-status");
      const response = await fetch(authStatusUrl, {
        method: "GET",
        credentials: "include",
        cache: "no-store" // Force fresh request
      });

      if (!response.ok) {
        throw new Error(`Auth status check failed: ${response.status}`);
      }

      const data = await response.json();
      const authenticated = data.isAuthenticated && data.hasValidToken;

      // Clear any cached state and force update
      setIsAuthenticated(authenticated);
      setCompanyInfo(null);

      onAuthChange?.(authenticated);

      // Only fetch company info if truly authenticated
      if (authenticated) {
        Sentry.addBreadcrumb({ category: "quickbooks", message: "QB auth verified (client)", level: "info" });
        await fetchCompanyInfo();
      } else {
        Sentry.addBreadcrumb({ category: "quickbooks", message: "QB not authenticated (client)", level: "info" });
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { category: "quickbooks", flow: "auth_status_client" } });
      console.error("QB auth-status check failed:", error instanceof Error ? error.message : String(error));
      setError('Failed to check authentication status');
      setIsAuthenticated(false);
      onAuthChange?.(false);
      setCompanyInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      setCompanyInfoLoading(true);
      const response = await apiRequest("GET", "/api/quickbooks/test");
      
      // Check if the response was successful
      if (!response.ok) {
        console.log('Company info fetch failed - likely not authenticated');
        setCompanyInfo(null);
        return;
      }
      
      const data = await response.json();
      
      if (data.companyInfo) {
        // Handle direct company info format from our API
        const company = data.companyInfo;
        setCompanyInfo({
          Name: company.CompanyName || company.Name,
          CompanyType: company.CompanyType,
          LegalName: company.LegalName,
          Country: company.Country
        });
      } else {
        setCompanyInfo(null);
      }
    } catch (error) {
      console.log('Company info not available - user needs to authenticate first');
      // Silently fail - this is expected when not authenticated
      setCompanyInfo(null);
    } finally {
      setCompanyInfoLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Getting QuickBooks auth URL...');
      const response = await apiRequest("GET", "/api/quickbooks/auth-url");
      const data = await response.json();
      
      console.log('Auth URL response:', data);
      
      if (data.authUrl) {
        console.log('Redirecting to QuickBooks:', data.authUrl);
        // Open QuickBooks authorization in the same window
        window.location.href = data.authUrl;
      } else {
        console.error('No auth URL in response:', data);
        setError('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error getting QuickBooks auth URL:', error);
      setError('Failed to start QuickBooks connection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await apiRequest("POST", "/api/quickbooks/signout");
      setIsAuthenticated(false);
      onAuthChange?.(false);
    } catch (error) {
      console.error('Error disconnecting from QuickBooks:', error);
      setError('Failed to disconnect from QuickBooks');
    } finally {
      setIsLoading(false);
    }
  };

  const readStoredSetting = async (response: Response): Promise<boolean> => {
    const data = await response.json();
    if (typeof data?.useHistoricalPricing !== "boolean") {
      throw new Error("QuickBooks settings response has no useHistoricalPricing");
    }
    return data.useHistoricalPricing;
  };

  const loadHistoricalPricingSetting = async () => {
    try {
      const response = await apiRequest("GET", "/api/quickbooks/settings");
      setUseHistoricalPricing(await readStoredSetting(response));
      setSettingsError(null);
    } catch (error) {
      console.error('Error loading QuickBooks settings:', error);
      setUseHistoricalPricing(null);
      setSettingsError("Couldn't load this setting. Reload the page to try again.");
    }
  };

  const saveHistoricalPricingSetting = async (enabled: boolean) => {
    try {
      setSettingsLoading(true);
      const response = await apiRequest("PUT", "/api/quickbooks/settings", {
        useHistoricalPricing: enabled
      });
      setUseHistoricalPricing(await readStoredSetting(response));
      setSettingsError(null);
    } catch (error) {
      console.error('Error saving QuickBooks settings:', error);
      setSettingsError("Couldn't save this setting. It is unchanged.");
    } finally {
      setSettingsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-center text-gray-600">Checking QuickBooks connection...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={cardRef}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isAuthenticated ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertCircle className="h-5 w-5 text-orange-500" />
          )}
          QuickBooks Integration
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4" data-testid="alert-quickbooks-auth-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex gap-2">
          {isAuthenticated ? (
            <Button 
              variant="outline" 
              onClick={handleDisconnect}
              disabled={isLoading}
              data-testid="button-disconnect-quickbooks"
            >
              Disconnect from QuickBooks
            </Button>
          ) : (
            <Button 
              onClick={handleConnect}
              disabled={isLoading}
              data-testid="button-connect-quickbooks"
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Connect to QuickBooks
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              checkAuthStatus();
            }}
            disabled={isLoading}
            data-testid="button-refresh-quickbooks-status"
          >
            {isLoading ? 'Refreshing...' : 'Refresh Status'}
          </Button>
        </div>
        
        {isAuthenticated && (
          <>
            <p className="text-sm text-green-600 mt-2">
              ✓ Ready to create invoice drafts
            </p>
            
            {/* Company Information Section */}
            {companyInfoLoading ? (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Loading company information...</p>
              </div>
            ) : companyInfo ? (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Connected QuickBooks Account</h4>
                <div className="space-y-1">
                  <p className="text-sm"><span className="font-medium">Company:</span> {companyInfo.Name}</p>
                  {companyInfo.LegalName && companyInfo.LegalName !== companyInfo.Name && (
                    <p className="text-sm"><span className="font-medium">Legal Name:</span> {companyInfo.LegalName}</p>
                  )}
                  {companyInfo.CompanyType && (
                    <p className="text-sm"><span className="font-medium">Type:</span> {companyInfo.CompanyType}</p>
                  )}
                  {companyInfo.Country && (
                    <p className="text-sm"><span className="font-medium">Country:</span> {companyInfo.Country}</p>
                  )}
                </div>
              </div>
            ) : null}
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="historical-pricing-toggle" className="text-sm font-medium">
                    Use Historical Customer Pricing
                  </Label>
                  <p className="text-sm text-gray-600">
                    Invoices always use the item's FreshOrder price first. When an item has no FreshOrder price, use the last price this customer was invoiced for it in QuickBooks, before the QuickBooks item's own price.
                  </p>
                </div>
                <Switch
                  id="historical-pricing-toggle"
                  checked={useHistoricalPricing ?? false}
                  onCheckedChange={saveHistoricalPricingSetting}
                  disabled={settingsLoading || useHistoricalPricing === null}
                  data-testid="switch-historical-pricing"
                />
              </div>
              {settingsError && (
                <p className="mt-2 text-sm text-red-600" data-testid="text-historical-pricing-error">{settingsError}</p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}